import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Footer, HelpText, Field } from './SiteConfig.stc';
import * as actions from '../../../redux/actions';
import Radio, { RadioGroup } from '../../Radio';
import Button from '../../Button';
import Modal from '../../Modal';
import Input from '../../Input';
import Label from '../../Label';
import Table from '../../Table';
import Alert from '../../Alert';
import Text from '../../Text';

import {
    getConfig,
    getSiteById,
    getSiteMutationError,
    getSiteMutationSuccess,
    getAuthUser,
} from '../../../redux/selectors';
import { useMemo } from 'react';
import validateMembership from '../../../util/validateMembership';

function SiteConfig({ siteId, onClose }) {
    const dispatch = useDispatch();
    const user = useSelector(getAuthUser);
    const { hostInfo } = useSelector(getConfig);
    const site = useSelector(getSiteById(siteId));
    const [siteUrl, setUrl] = useState(site.domain || '');
    const [projectName, setProjectName] = useState(site.name || '');
    const [siteUrlType, setUrlType] = useState(site.domainType || 'DORIK_IO');
    const errorMessage = useSelector(getSiteMutationError);
    const successMessage = useSelector(getSiteMutationSuccess);

    const customDomainAvailable = ['PLAN_1', 'PLAN_2', 'PLAN_3'];
    const enabled = validateMembership(customDomainAvailable, user);

    useEffect(() => {
        dispatch(actions.clearSiteMessage());
        return () => dispatch(actions.clearSiteMessage());
    }, [dispatch]);

    const submitConfig = async (e) => {
        e.preventDefault();
        if (siteUrlType !== 'DRAFT' && siteUrl === '') return;
        const publish = siteUrlType !== 'DRAFT' && site.domain !== siteUrl;

        dispatch(
            actions.configDomain({
                publish,
                id: site.id,
                domain: siteUrl,
                name: projectName,
                domainType: siteUrlType,
            })
        );
    };

    const inputFieldInlineStyle = useMemo(
        () => ({ marginTop: 25, height: 75 }),
        []
    );

    return (
        <Modal
            size="md"
            onClose={onClose}
            header={
                <div>
                    <strong>SITE SETTINGS</strong> ({site.url || site.id})
                </div>
            }
            footer={
                <Footer>
                    <div>
                        <a
                            href="https://help.dorik.io/site-settings-integrations/"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Documentation
                        </a>
                    </div>
                    <div>
                        <Button onClick={onClose} type="link">
                            Cancel
                        </Button>
                        <Button onClick={submitConfig} type="primary">
                            Save Changes
                        </Button>
                    </div>
                </Footer>
            }
        >
            <Alert type="warning" className="mb-30">
                <strong>Warning</strong> Site config changes will affect your
                website URL accessibility
            </Alert>
            <div
                className="flex"
                style={{ marginTop: 10, alignItems: 'start' }}
            >
                <div className="col-md-1/2">
                    <div className="column-inner">
                        <form onSubmit={submitConfig}>
                            <Field>
                                <Label>Project Name</Label>
                                <Input
                                    value={projectName}
                                    onChange={(e) =>
                                        setProjectName(e.target.value)
                                    }
                                />
                            </Field>

                            <p style={{ marginBottom: 10, marginTop: 0 }}>
                                <strong>Site URL Settings</strong>
                            </p>
                            <RadioGroup
                                type="inline"
                                value={siteUrlType}
                                onChange={(e) => setUrlType(e.target.value)}
                            >
                                <Radio value="CUSTOM" disabled={!enabled}>
                                    Custom Domain (without WWW)
                                </Radio>
                                <Radio value="DORIK_IO">
                                    Dorik.io Subdomain
                                </Radio>
                                <Radio value="DRAFT">Unpublish Website</Radio>
                            </RadioGroup>

                            <Field style={inputFieldInlineStyle}>
                                {siteUrlType !== 'DRAFT' && (
                                    <Input
                                        value={siteUrl}
                                        groupAppend={
                                            siteUrlType === 'DORIK_IO' &&
                                            '.dorik.io'
                                        }
                                        placeholder={
                                            siteUrlType === 'DORIK_IO'
                                                ? 'subdomain'
                                                : 'Enter your domain'
                                        }
                                        onChange={(e) => setUrl(e.target.value)}
                                    />
                                )}
                                {errorMessage && (
                                    <Text
                                        type="error"
                                        component="span"
                                        className="error"
                                    >
                                        {errorMessage}
                                    </Text>
                                )}
                                {successMessage && (
                                    <Text
                                        type="success"
                                        component="span"
                                        className="error"
                                    >
                                        {successMessage}
                                    </Text>
                                )}
                            </Field>
                        </form>
                    </div>
                </div>
                {siteUrlType === 'CUSTOM' && (
                    <div className="col-md-1/2" style={{ fontSize: 14 }}>
                        <HelpText>
                            <p>
                                After updating your custom domain set the DNS
                                records listed below:
                            </p>
                            <Table data={hostInfo} />
                            <p>
                                <a
                                    href="https://help.dorik.io/site-settings-integrations/custom-domain-configuration"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    How to Configure Custom Domain
                                </a>
                            </p>
                            <p>
                                <strong>Note:</strong> The domain change may
                                take around 1 to 24 hours to get updated. WWW
                                domain will be forwarded to bare(non-www)
                                domain.
                            </p>
                        </HelpText>
                    </div>
                )}
            </div>
        </Modal>
    );
}

export default React.memo(SiteConfig);
